import React from 'react';
import { X, Navigation, MapPin, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getOrderNumberSuffix } from '@/utils/markerUtils';

/**
 * Floating panel that displays turn-by-turn directions for a delivery run
 */
const DirectionsPanel = ({ 
  directionSteps, 
  routeInfo, 
  onClose,
  runName 
}) => {
  if (!directionSteps || directionSteps.length === 0) {
    return null;
  }

  // Get total step count
  const totalSteps = directionSteps.reduce((acc, leg) => acc + leg.steps.length, 0);

  return (
    <div 
      className="bg-white/95 backdrop-blur-sm rounded-xl shadow-soft-lg border border-stone-200/50 flex flex-col overflow-hidden max-h-[calc(100vh-100px)]"
      style={{ width: '320px' }}
      data-testid="directions-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-200/50 bg-primary/5/80 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Navigation className="h-4 w-4 text-primary" />
          <div>
            <h3 className="font-medium text-sm text-stone-800">Directions</h3>
            <p className="text-xs text-stone-500">
              {runName} • {routeInfo?.distance} mi • {routeInfo?.duration} min
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="h-7 w-7 p-0 hover:bg-primary/10"
          data-testid="close-directions-btn"
        >
          <X className="h-4 w-4 text-stone-500" />
        </Button>
      </div>

      {/* Summary bar */}
      <div className="px-4 py-2 bg-stone-50/50 border-b border-stone-200/30 flex-shrink-0">
        <div className="text-xs text-stone-600">
          <span className="font-medium">{directionSteps.length}</span> stop{directionSteps.length !== 1 ? 's' : ''} • 
          <span className="font-medium ml-1">{totalSteps}</span> turn{totalSteps !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Directions content - scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Start point */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-green-600 text-white flex items-center justify-center flex-shrink-0">
              <MapPin className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xs text-stone-500 font-medium">START</div>
              <div className="text-sm font-medium text-stone-800">Las Vegas Bouquet</div>
            </div>
          </div>
        </div>

        {/* Direction legs */}
        {directionSteps.map((leg, legIndex) => {
          const orderSuffix = leg.order ? getOrderNumberSuffix(leg.order.internal_order_number) : '?';
          const recipientName = leg.order?.recipient_name || 'Unknown';
          
          return (
            <div key={leg.order?.id || legIndex} className="border-b border-gray-100 last:border-b-0">
              {/* Leg header - Stop info */}
              <div className="px-4 py-2.5 bg-primary/5/50 border-b border-blue-100/50">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-primary text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {leg.stopSequence}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-primary truncate">
                      Stop {leg.stopSequence}: Order #{orderSuffix}
                    </div>
                    <div className="text-xs text-primary truncate">{recipientName}</div>
                  </div>
                  <div className="text-xs text-stone-500 flex-shrink-0">
                    {leg.legDistance}
                  </div>
                </div>
              </div>

              {/* Direction steps for this leg */}
              <div className="divide-y divide-gray-50">
                {leg.steps.map((step, stepIndex) => (
                  <div 
                    key={step.id} 
                    className="px-4 py-2.5 flex items-start gap-3 hover:bg-stone-50/50 transition-colors"
                  >
                    {/* Maneuver icon */}
                    <div className="w-7 h-7 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0 text-lg font-medium text-stone-700">
                      {step.icon}
                    </div>
                    
                    {/* Instruction and distance */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-800 leading-snug">
                        {step.instruction}
                      </div>
                    </div>
                    
                    {/* Distance */}
                    <div className="text-xs text-stone-500 flex-shrink-0 mt-0.5">
                      {step.distance}
                    </div>
                  </div>
                ))}

                {/* Arrival indicator */}
                <div className="px-4 py-2.5 flex items-center gap-3 bg-green-50/50">
                  <div className="w-7 h-7 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Flag className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-green-700 font-medium">
                      Arrive: {recipientName}
                    </div>
                    <div className="text-xs text-green-600">
                      Order #{orderSuffix}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DirectionsPanel;
